import "ol/ol.css";
import "ol-ext/dist/ol-ext.css";
import "ol-popup/src/ol-popup.css";
import {Map, View, Feature, Collection} from "ol";
import {Circle, LineString, MultiPoint, Point, Polygon} from "ol/geom";
import {GeoJSON, KML, MVT} from "ol/format";
import {Vector as VectorSource, VectorTile as VectorTileSource} from "ol/source";
import {FullScreen, defaults as defaultControls} from "ol/control";
import {Fill, Stroke, Style, Icon} from "ol/style";
import {fromLonLat, transform, transformExtent} from "ol/proj";
import {getTileLayer} from "./TileLayer";
import VectorLayer from "ol/layer/Vector";
import Translate from "ol/interaction/Translate";
import {createEmpty, extend} from "ol/extent";
import {Draw, Select} from "ol/interaction";
import {VectorTile} from "ol/layer";
import {createXYZ} from "ol/tilegrid";
import {singleClick} from "ol/events/condition";
import cartodb from "../../../constants/cartodb";
import Popup from "ol-popup";
import {infowindowContentLayerCarto} from "../../../components/Maps/infowindowContent";

const {serverUrl, apiKey} = cartodb;

let olMap = null;
let draw = null;
let selectInteraction = null;
let defaultCity;
defaultCity = {
	city: "Maidenhead",
	lat: 51.5180094,
	lng: -0.7637034,
};
const convertToRGB = (hex) => {
	let red = parseInt(hex[1] + hex[2], 16);
	let green = parseInt(hex[3] + hex[4], 16);
	let blue = parseInt(hex[5] + hex[6], 16);
	return [red, green, blue];
};
const showPopup = (data, eventsMarker) => {
	let popup = new Popup();
	olMap.addOverlay(popup);
	popup.getElement().addEventListener(
		"click",
		function (e) {
			if (eventsMarker.close) eventsMarker.close();
		},
		false
	);
	olMap.on("singleclick", function (evt) {
		popup.hide();
		popup.setOffset([0, 0]);
		const feature = olMap.forEachFeatureAtPixel(evt.pixel, function (feature) {
			return feature;
		});
		if (feature === data.marker.getSource().getFeatures()[0]) {
			if (typeof data._opts.onClick === "function") {
				evt.info = data._opts.info;
				data._opts.onClick(evt, {lng: data.latLng.lng, lat: data.latLng.lat});
			}
			let coordinates = feature.getGeometry().getCoordinates();
			popup.setOffset([0, -40]);
			popup.show(coordinates, data._opts.infoWindow.content);
		} else {
			popup.hide();
		}
	});
};
const dragAndDrop = (typeObject, _opts) => {
	const translate = new Translate({features: new Collection([typeObject])});
	olMap.addInteraction(translate);
	translate.on("translateend", function () {
		const getPosition = transform(typeObject.getGeometry().getCoordinates(), "EPSG:3857", "EPSG:4326");
		inverseGeocoder(
			{lat: getPosition[1], lng: getPosition[0]},
			function (result) {
				_opts.onDragend(result, {
					lat: getPosition[1],
					lng: getPosition[0],
				});
			},
			{mapsApiKey: process.env.VUE_APP_HERE_MAP_API_KEY}
		);
	});
	olMap.on("pointermove", function (event) {
		if (event.dragging) return;
		let hit = olMap.hasFeatureAtPixel(olMap.getEventPixel(event.originalEvent));
		olMap.getTargetElement().style.cursor = hit ? "pointer" : "";
	});
};

/**
 * Determina la Inicialización de OlAnuket y es una función requerida al ejecutar su aplicación para la correcta configuración del mapa base.
 * @param {string} mapContainer Contenedor del mapa.
 * @param {Object} options Objeto con opciones para la inicialización del mapa.
 * @property {string} options.mapType Define el tipo de mapa a visualizar(google, here y osm).
 * @property {Object} options.vectorLayer Objeto que emplea la visualización de los datos vectoriales del mapa.
 * @property {Object} options.controls Objeto con propiedades que permiten la interacción con el mapa.
 * @property {boolean} options.controls.streetView Habilita o deshabilita el control street view en el mapa.
 * @property {boolean} options.controls.zoom Habilita o deshabilita el control que permite el cambio del nivel de Zoom del mapa.
 * @property {boolean} options.controls.fullScreen Habilita o deshabilita el control de abrir el mapa en pantalla completa.
 * @property {boolean} options.geolocation Habilita o deshabilita la ubicación geográfica en el mapa.
 * @property {number} options.lat Latitude para fijar el mapa.
 * @property {number} options.lng Longitude para fijar el mapa.
 * @property {number} options.zoom Define el Zoom sobre el mapa generado.
 *
 * @returns {Object} Objeto del mapa generado.
 */
export function initializeOlMap(mapContainer = "", options = {}) {
	let _opts = {
		el: "olMap",
		mapType: options.mapType,
		vectorLayer: options.vectorLayer,
		controls: {
			streetView: false,
			zoom: true,
			fullScreen: true,
		},
		geolocation: false,
		lat: defaultCity.lat,
		lng: defaultCity.lng,
		zoom: 13,
	};
	if (mapContainer === undefined || mapContainer === "") {
		console.warn("run: _divMap must be an String");
		return false;
	} else {
		_opts.el = mapContainer;
	}
	if (options) {
		if (typeof options === "object" && Object.entries(options).length > 0) {
			if (typeof options.controls === "object" && Object.entries(options.controls).length > 0) {
				let controls = Object.entries(options.controls);
				for (let control of controls) {
					if (control[0] !== undefined && control[0] !== null) {
						if (control[1] === true || control[1] === false) {
							_opts.controls[control[0]] = control[1];
						}
					}
				}
			}
			if (options.geolocation !== undefined && options.geolocation !== null) {
				if (options.geolocation) {
					_opts.geolocation = options.geolocation;
				}
			}
			if (
				options.lat !== undefined &&
				options.lat !== null &&
				options.lng !== undefined &&
				options.lng !== null
			) {
				let coords = {lat: options.lat, lng: options.lng};
				if (typeof coords !== "object") {
					console.warn("run: coords must be an Object");
					return false;
				} else {
					if (!coords.hasOwnProperty("lat") && !coords.hasOwnProperty("lng")) {
						console.warn("run: missing lat or lng attributes");
						return false;
					} else {
						_opts.lat = options.lat;
						_opts.lng = options.lng;
					}
				}
			}
			if (options.zoom !== undefined && options.zoom !== null) {
				if (parseInt(options.zoom) >= 0 && parseInt(options.zoom) <= 18) {
					_opts.zoom = options.zoom;
				}
			}
		} else {
			console.warn("run: options is not an object or is empty");
			return false;
		}
	} else {
		console.info("run: now using current configuration");
	}
	olMap = new Map({
		target: _opts.el,
		layers: [getTileLayer(_opts.mapType), _opts.vectorLayer],
		view: new View({
			projection: "EPSG:3857",
			center: fromLonLat([_opts.lng, _opts.lat]),
			minZoom: 0,
			maxZoom: 18,
			zoom: _opts.zoom,
		}),
		controls: _opts.controls.fullScreen ? defaultControls().extend([new FullScreen()]) : null,
	});
	if (_opts.geolocation) getCurrentPosition();
	return olMap;
}

/**
 * Agrega un marcador en el mapa.
 * @param {Object} latLng Objeto de coordenadas para la ubicación del marcador.
 * @param {Object} options (opcional) Objeto con propiedades extras para el marcador.
 * @property {string} options.title Título del marcador.
 * @property {string} options.icon Establece un icono al marcador.
 * @property {boolean} options.draggable Habilita o deshabilita la acción de arrastrar el marcador en el mapa.
 * @property {Object} options.infoWindow Objeto con opciones para establecer una ventana de información sobre el marcador.
 * @property {boolean} options.infoWindow.show Habilita o deshabilita la ventana de información.
 * @property {string} options.infoWindow.content Define el contenido para la ventana de información.
 * @property {boolean} options.info Pasar información en el Objeto del marcador.
 * @property {function} options.onClick Función anónima que permite capturar el evento y las coordenadas al hacer clic sobre el marcador.
 * @property {function} options.onDragend Función anónima que permite capturar el evento y las nuevas coordenadas al arrastrar el marcador(options.draggable= true).
 *
 * @returns {Object} Objeto de instancia de tipo VectorLayer.
 */
export function addMarker(latLng, options) {
	let _opts = {
		title: "Current Position",
		icon: null,
		draggable: true,
		activatePopup: true,
		infoWindow: {
			content: "",
		},
		info: null,
		onClick: null,
		onDragend: null,
	};
	if (options) {
		if (typeof options === "object" && Object.entries(options).length > 0) {
			if (options.title !== null) _opts.title = options.title;
			if (options.icon !== null) _opts.icon = options.icon;
			if (options.draggable !== undefined) {
				if (options.draggable !== null) {
					if (options.draggable) {
						_opts.draggable = options.draggable;
					}
				}
			}
			if (options.activatePopup !== undefined) {
				if (options.activatePopup !== null) {
					if (options.activatePopup) {
						_opts.showPopup = options.activatePopup;
					}
				}
			}
			if (options.infoWindow !== undefined) {
				if (options.infoWindow !== null) {
					_opts.infoWindow = options.infoWindow;
				}
			}
			if (options.info !== undefined) {
				if (options.info !== null) {
					_opts.info = options.info;
				}
			}
			if (options.label !== undefined) {
				if (options.label !== null) {
					_opts.label = options.label;
				}
			}
			if (options.onClick !== null) {
				if (typeof options.onClick === "function") {
					_opts.onClick = options.onClick;
				}
			}
			if (options.onDragend != null) {
				if (typeof options.onDragend === "function") {
					_opts.onDragend = options.onDragend;
				}
			}
		} else {
			console.warn("run: options is not an object or is empty");
			return false;
		}
	}
	const markerFeature = new Feature({
		type: "Point",
		geometry: new Point(fromLonLat([latLng.lng, latLng.lat])),
	});
	const styles = {
		Point: new Style({
			image: new Icon({
				anchor: [0.5, 46],
				anchorXUnits: "fraction",
				anchorYUnits: "pixels",
				src: _opts.icon ? _opts.icon : "",
			}),
		}),
	};
	let marker = new VectorLayer({
		source: new VectorSource({
			features: [markerFeature],
		}),
		style: function (feature) {
			return styles[feature.get("type")];
		},
		zIndex: 100,
	});
	marker.info = _opts.info;
	if (_opts.showPopup) {
		showPopup({_opts, latLng, marker}, {close: options.onClose});
	} else {
		olMap.on("singleclick", function (evt) {
			const trailheads = olMap.getFeaturesAtPixel(evt.pixel, {
				layerFilter: (layer) => layer === marker,
			});
			if (trailheads.length > 0) {
				if (typeof _opts.onClick === "function") {
					evt.info = _opts.info;
					_opts.onClick(evt, {lng: latLng.lng, lat: latLng.lat});
				}
			}
		});
	}
	//   if (_opts.draggable) dragAndDrop(markerFeature, _opts);
	return marker;
}

/**
 * Agrega una colección de marcadores en el mapa.
 * @param {Array} coordinateList Matriz de coordenadas.
 *
 * @returns {Object} Objeto de instancia de tipo VectorLayer
 */
export function addGroupMarkers(coordinateList) {
	if (coordinateList.constructor !== [].constructor) {
		console.warn("addGroupMarkers: coordinates must be an array");
		return false;
	}
	if (coordinateList.length <= 1) {
		console.warn("addGroupMarkers: coordinates must have more than one element");
		return false;
	}
	if (olMap) {
		const groupMarkers = new MultiPoint(coordinateList).transform("EPSG:4326", "EPSG:3857");
		const groupMarkersFeature = new Feature({
			id: "Marker",
			type: "MultiPoint",
			geometry: groupMarkers,
		});
		const styles = {
			MultiPoint: new Style({
				image: new Icon({
					anchor: [0.5, 46],
					anchorXUnits: "fraction",
					anchorYUnits: "pixels",
					src: "https://openlayers.org/en/latest/examples/data/icon.png",
				}),
			}),
		};
		return new VectorLayer({
			source: new VectorSource({
				features: [groupMarkersFeature],
			}),
			style: function (feature) {
				return styles[feature.get("type")];
			},
		});
	} else {
		console.warning("addGroupMarkers: olMap is missing");
	}
}

/**
 * Dibuja un polígono en el mapa.
 * @param {Array} coordinates Matriz bidimensional de latitudes y longitudes.
 * @param {Object} options (opcional) Objeto con propiedades extras para el polígono.
 * @property {string} options.strokeColor Color de contorno en formato hexadecimal.
 * @property {number} options.strokeOpacity Opacidad del (color) de contorno.
 * @property {number} options.strokeWeight Ancho de la línea de contorno en píxeles.
 * @property {string} options.fillColor Color de relleno en formato hexadecimal.
 * @property {float} options.fillOpacity Opacidad del (color) de relleno en valores de 0.0 a 1.0.
 * @property {string} options.toRemove Limpia el mapa(all) o borra elementos especifico(polygons ó polylines).
 *
 * @returns {Object} Objeto de instancia de tipo VectorLayer
 */
export function drawPolygon(coordinates, options) {
	let _opts = {
		strokeColor: "#000000",
		strokeOpacity: 1,
		strokeWeight: 2,
		fillColor: "#0E51DC",
		fillOpacity: 0.2,
		toRemove: null,
	};
	if (olMap) {
		if (coordinates.constructor !== [].constructor) {
			console.warn("drawPolygon: paths must be an Array");
			return false;
		}
		if (coordinates.length <= 0) {
			console.warn("drawPolygon: paths must have at least item");
			return false;
		}
		if (options) {
			if (typeof options === "object" && Object.entries(options).length > 0) {
				if (options.strokeColor !== undefined) {
					if (options.strokeColor !== null) {
						if (options.strokeColor !== "") {
							_opts.strokeColor = options.strokeColor;
						}
					}
				}
				if (options.strokeOpacity !== undefined) {
					if (options.strokeOpacity !== null) {
						if (Number.isInteger(options.strokeOpacity)) {
							if (options.strokeOpacity >= 1 && options.strokeOpacity <= 10) {
								_opts.strokeOpacity = options.strokeOpacity;
							}
						}
					}
				}
				if (options.strokeWeight !== undefined) {
					if (options.strokeWeight !== null) {
						if (Number.isInteger(options.strokeWeight)) {
							if (options.strokeWeight >= 1 && options.strokeWeight <= 10) {
								_opts.strokeWeight = options.strokeWeight;
							}
						}
					}
				}
				if (options.fillColor !== undefined) {
					if (options.fillColor !== null) {
						if (options.fillColor !== "") {
							_opts.fillColor = options.fillColor;
						}
					}
				}
				if (options.fillOpacity !== undefined) {
					if (options.fillOpacity !== null) {
						if (parseFloat(options.fillOpacity) >= 0.0 && parseFloat(options.fillOpacity) <= 1.0) {
							_opts.fillOpacity = options.fillOpacity;
						}
					}
				}
				if (options.toRemove !== undefined) {
					if (options.toRemove !== null) {
						if (options.toRemove !== "") {
							_opts.toRemove = options.toRemove;
						}
					}
				}
			} else {
				console.warn("run: options is not an object or is empty");
				return false;
			}
		}
		switch (_opts.toRemove) {
			case "all":
				clearMap();
				break;
			case "polygons":
				removePolygons();
				break;
			case "polylines":
				removePolylines();
				break;
			default:
				break;
		}
		const polygonFigure = new Polygon(coordinates).transform("EPSG:4326", "EPSG:3857");
		const polygonFeature = new Feature({
			id: "Polygon",
			type: "Polygon",
			geometry: polygonFigure,
		});
		let colorFinalStroke = "";
		let colorFinalFill = "";
		if (/^#[0-9A-F]{6}$/i.test(_opts.strokeColor)) {
			let colorRgbStroke = convertToRGB(_opts.strokeColor);
			colorFinalStroke = `rgba(${colorRgbStroke[0]},${colorRgbStroke[1]}, ${colorRgbStroke[2]},${_opts.strokeOpacity}`;
		}
		if (/^#[0-9A-F]{6}$/i.test(_opts.fillColor)) {
			let colorRgbFill = convertToRGB(_opts.fillColor);
			colorFinalFill = `rgba(${colorRgbFill[0]},${colorRgbFill[1]}, ${colorRgbFill[2]},${_opts.fillOpacity}`;
		}
		const styles = {
			Polygon: new Style({
				stroke: new Stroke({
					color: colorFinalStroke ? colorFinalStroke : _opts.strokeColor,
					width: _opts.strokeWeight,
				}),
				fill: new Fill({
					color: colorFinalFill ? colorFinalFill : _opts.fillColor,
				}),
			}),
		};
		return new VectorLayer({
			source: new VectorSource({
				features: [polygonFeature],
			}),
			style: function (feature) {
				return styles[feature.get("type")];
			},
		});
	} else {
		console.warn("drawPolygon: olMap is not defined");
		return false;
	}
}

/**
 * Dibuja una polilínea en el mapa.
 * @param {Array} coordinates Colección de coordenadas.
 * @param {Object} options (opcional) Objeto con propiedades extras para la polilínea.
 * @property {string} options.strokeColor Color de la polilínea en formato hexadecimal.
 * @property {number} options.strokeOpacity Opacidad de la polilínea en valores de 0.0 a 1.0.
 * @property {number} options.strokeWeight Ancho de polilínea en píxeles.
 * @property {string} options.toRemove Limpia el mapa(all) o borra elementos especifico(polygons ó polylines).
 *
 * @returns {Object} Objeto de instancia de tipo VectorLayer
 */
export function drawPolyline(coordinates, options) {
	let _opts = {
		strokeColor: "#0E51DC",
		strokeOpacity: 1,
		strokeWeight: 2,
		toRemove: null,
	};
	if (olMap) {
		if (coordinates.constructor !== [].constructor) {
			console.warn("drawPolygon: paths must be an Array");
			return false;
		}
		if (coordinates.length <= 0) {
			console.warn("drawPolygon: paths must have at least item");
			return false;
		}
		if (options) {
			if (typeof options === "object" && Object.entries(options).length > 0) {
				if (options.strokeColor !== undefined) {
					if (options.strokeColor !== null) {
						if (options.strokeColor !== "") {
							_opts.strokeColor = options.strokeColor;
						}
					}
				}
				if (options.strokeOpacity !== undefined) {
					if (options.strokeOpacity !== null) {
						if (Number.isInteger(options.strokeOpacity)) {
							if (options.strokeOpacity >= 1 && options.strokeOpacity <= 10) {
								_opts.strokeOpacity = options.strokeOpacity;
							}
						}
					}
				}
				if (options.strokeWeight !== undefined) {
					if (options.strokeWeight !== null) {
						if (Number.isInteger(options.strokeWeight)) {
							if (options.strokeWeight >= 1 && options.strokeWeight <= 10) {
								_opts.strokeWeight = options.strokeWeight;
							}
						}
					}
				}
				if (options.toRemove !== undefined) {
					if (options.toRemove !== null) {
						if (options.toRemove !== "") {
							_opts.toRemove = options.toRemove;
						}
					}
				}
			} else {
				console.warn("run: options is not an object or is empty");
				return false;
			}
		}
		switch (_opts.toRemove) {
			case "all":
				clearMap();
				break;
			case "polygons":
				removePolygons();
				break;
			case "polylines":
				removePolylines();
				break;
			default:
				break;
		}
		const polylineRoute = new LineString(coordinates).transform("EPSG:4326", "EPSG:3857");
		const polylineFeature = new Feature({
			id: "Polyline",
			type: "Polyline",
			geometry: polylineRoute,
		});
		let colorFinalStroke = "";
		if (/^#[0-9A-F]{6}$/i.test(_opts.strokeColor)) {
			let colorRgbStroke = convertToRGB(_opts.strokeColor);
			colorFinalStroke =
				"rgba" +
				"(" +
				`${colorRgbStroke[0] + "," + colorRgbStroke[1] + "," + colorRgbStroke[2]}` +
				", " +
				_opts.strokeOpacity +
				")";
		}
		const styles = {
			Polyline: new Style({
				stroke: new Stroke({
					color: colorFinalStroke ? colorFinalStroke : _opts.strokeColor,
					width: _opts.strokeWeight,
				}),
			}),
		};
		return new VectorLayer({
			source: new VectorSource({
				features: [polylineFeature],
			}),
			style: function (feature) {
				return styles[feature.get("type")];
			},
		});
	} else {
		console.warn("drawPolygon: olMap is not defined");
		return false;
	}
}

/**
 * Dibuja un círculo en el mapa.
 * @param {Array} coordinates Colección de coordenadas.
 * @param {number} radius Radio del círculo en metros.
 * @param {Object} options (opcional) Objeto con propiedades extras para el círculo.
 * @property {string} options.strokeColor Color de contorno en formato hexadecimal.
 * @property {number} options.strokeOpacity Opacidad del color de contorno.
 * @property {number} options.strokeWeight Ancho de la línea de contorno en píxeles.
 * @property {string} options.fillColor Color de relleno en formato hexadecimal.
 * @property {float} options.fillOpacity Opacidad del color de relleno en valores de 0.0 a 1.0.
 * @property {string} options.toRemove Limpia el mapa(all) o borra elementos especifico(polygons ó polylines).
 *
 * @returns {Object} Objeto de instancia de tipo VectorLayer
 */
export function drawCircle(coordinates, radius = 200, options) {
	let _opts = {
		radius: 100,
		strokeColor: "#3878c7",
		strokeOpacity: 1,
		strokeWeight: 0.5,
		fillColor: "#ffff00",
		fillOpacity: 0.1,
		toRemove: null,
	};
	if (olMap) {
		if (coordinates.constructor !== [].constructor) {
			console.warn("drawCircle: coords must be an Array");
			return false;
		}
		if (coordinates.length <= 0) {
			console.warn("drawCircle: the coords size is not correct");
			return false;
		}
		if (radius === undefined || radius == null) {
			console.warn("drawCircle: The radius is required");
			return false;
		}
		if (Number.isInteger(radius)) {
			_opts.radius = radius;
		} else {
			console.warn("drawCircle: The radius is not a number");
			return false;
		}
		if (options) {
			if (typeof options === "object" && Object.entries(options).length > 0) {
				if (options.strokeColor !== undefined) {
					if (options.strokeColor !== null) {
						if (options.strokeColor !== "") {
							_opts.strokeColor = options.strokeColor;
						}
					}
				}
				if (options.strokeOpacity !== undefined) {
					if (options.strokeOpacity !== null) {
						if (Number.isInteger(options.strokeOpacity)) {
							if (options.strokeOpacity >= 1 && options.strokeOpacity <= 10) {
								_opts.strokeOpacity = options.strokeOpacity;
							}
						}
					}
				}
				if (options.strokeWeight !== undefined) {
					if (options.strokeWeight !== null) {
						if (Number.isInteger(options.strokeWeight)) {
							if (options.strokeWeight >= 1 && options.strokeWeight <= 10) {
								_opts.strokeWeight = options.strokeWeight;
							}
						}
					}
				}
				if (options.fillColor !== undefined) {
					if (options.fillColor !== null) {
						if (options.fillColor !== "") {
							_opts.fillColor = options.fillColor;
						}
					}
				}
				if (options.fillOpacity !== undefined) {
					if (options.fillOpacity !== null) {
						if (parseFloat(options.fillOpacity) >= 0.0 && parseFloat(options.fillOpacity) <= 1.0) {
							_opts.fillOpacity = options.fillOpacity;
						}
					}
				}
				if (options.toRemove !== undefined) {
					if (options.toRemove !== null) {
						if (options.toRemove !== "") {
							_opts.toRemove = options.toRemove;
						}
					}
				}
				if (options.editable !== undefined) {
					if (options.editable !== null) {
						if (options.editable) {
							_opts.editable = options.editable;
						}
					}
				}
				if (options.draggable !== undefined) {
					if (options.draggable !== null) {
						if (options.draggable) {
							_opts.draggable = options.draggable;
						}
					}
				}
			} else {
				console.warn("run: options is not an object or is empty");
				return false;
			}
		}
		switch (_opts.toRemove) {
			case "all":
				clearMap();
				break;
			case "polygons":
				removePolygons();
				break;
			case "polylines":
				removePolylines();
				break;
			default:
				break;
		}
		const circleFigure = new Circle(transform(coordinates, "EPSG:4326", "EPSG:3857"), _opts.radius);
		const circleFeature = new Feature({
			type: "Circle",
			geometry: circleFigure,
		});
		let colorFinalStroke = "";
		let colorFinalFill = "";
		if (/^#[0-9A-F]{6}$/i.test(_opts.strokeColor)) {
			let colorRgbStroke = convertToRGB(_opts.strokeColor);
			colorFinalStroke = `rgba(${colorRgbStroke[0]},${colorRgbStroke[1]}, ${colorRgbStroke[2]},${_opts.strokeOpacity}`;
		}
		if (/^#[0-9A-F]{6}$/i.test(_opts.fillColor)) {
			let colorRgbFill = convertToRGB(_opts.fillColor);
			colorFinalFill = `rgba(${colorRgbFill[0]},${colorRgbFill[1]}, ${colorRgbFill[2]},${_opts.fillOpacity}`;
		}
		const styles = {
			Circle: new Style({
				stroke: new Stroke({
					color: colorFinalStroke ? colorFinalStroke : _opts.strokeColor,
					width: _opts.strokeWeight,
				}),
				fill: new Fill({
					color: colorFinalFill ? colorFinalFill : _opts.fillColor,
				}),
			}),
		};
		const vectorSource = new VectorSource({
			projection: "EPSG:4326",
			features: [circleFeature],
		});
		return new VectorLayer({
			source: vectorSource,
			style: function (feature) {
				return styles[feature.get("type")];
			},
		});
	} else {
		console.warn("drawCircle: olMap is not defined");
		return false;
	}
}

/**
 * Dibuja una ruta en el mapa usando polilíneas.
 * @param {Array} origin Matriz con dos elementos: longitud y latitud.
 * @param {Array} destination Matriz con dos elementos: longitud y latitud.
 * @param {Object} options (opcional) Objeto con propiedades extras para la ruta.
 * @property {string} options.toRemove Habilita o deshabilita un marcador al inicio y al final de la ruta.
 * @property {string} options.strokeColor Color de la polilínea en formato hexadecimal.
 * @property {number} options.strokeOpacity Opacidad de la polilínea en valores de 0.0 a 1.0.
 * @property {number} options.strokeWeight Ancho de polilínea en píxeles.
 *
 * @returns {Object} Objeto de instancia de tipo VectorLayer
 */
export function drawRoute(origin, destination, options) {
	let _opts = {
		markers: false,
		strokeColor: "#0E51DC",
		strokeOpacity: 0.8,
		strokeWeight: 7,
	};
	if (origin.constructor !== [].constructor) {
		console.warn("drawRoute: origin must be an Array");
		return false;
	}

	if (destination.constructor !== [].constructor) {
		console.warn("drawRoute: destination must be an Array");
		return false;
	}
	if (options) {
		if (typeof options === "object" && Object.entries(options).length > 0) {
			if (options.markers !== undefined) {
				if (options.markers !== null) {
					if (options.markers) {
						_opts.markers = options.markers;
					}
				}
			}
			if (options.strokeColor !== undefined) {
				if (options.strokeColor !== null) {
					if (options.strokeColor !== "") {
						_opts.strokeColor = options.strokeColor;
					}
				}
			}
			if (options.strokeOpacity !== undefined) {
				if (options.strokeOpacity !== null) {
					if (parseFloat(options.strokeOpacity) >= 0.0 && parseFloat(options.strokeOpacity) <= 1.0) {
						_opts.strokeOpacity = options.strokeOpacity;
					}
				}
			}
			if (options.strokeWeight !== undefined) {
				if (options.strokeWeight !== null) {
					if (Number.isInteger(options.strokeWeight)) {
						if (options.strokeWeight >= 1 && options.strokeWeight <= 10) {
							_opts.strokeWeight = options.strokeWeight;
						}
					}
				}
			}
		} else {
			console.warn("run: options is not an object or is empty");
			return false;
		}
	}
	if (origin.length > 0 && destination.length > 0) {
		if (olMap) {
			let locations = [origin, destination];
			let route = new LineString(locations).transform("EPSG:4326", "EPSG:3857");
			const routeFeature = new Feature({
				id: "route",
				type: "Route",
				geometry: route,
			});
			let startMarker = new Feature({
				type: "icon",
				geometry: new Point(route.getFirstCoordinate()),
			});
			let endMarker = new Feature({
				type: "icon",
				geometry: new Point(route.getLastCoordinate()),
			});
			let geoMarkerInit = new Feature({
				type: "geoMarker",
				geometry: startMarker.getGeometry().clone(),
			});
			const geoMarkerLast = new Feature({
				type: "geoMarker",
				geometry: endMarker.getGeometry().clone(),
			});
			let colorFinalStroke = "";
			if (/^#[0-9A-F]{6}$/i.test(_opts.strokeColor)) {
				let colorRgbStroke = convertToRGB(_opts.strokeColor);
				colorFinalStroke =
					"rgba" +
					"(" +
					`${colorRgbStroke[0] + "," + colorRgbStroke[1] + "," + colorRgbStroke[2]}` +
					", " +
					_opts.strokeOpacity +
					")";
			}
			const styles = {
				Route: new Style({
					stroke: new Stroke({
						color: colorFinalStroke ? colorFinalStroke : _opts.strokeColor,
						width: _opts.strokeWeight,
					}),
				}),
				geoMarker: new Style({
					image: new Icon({
						anchor: [0.5, 46],
						anchorXUnits: "fraction",
						anchorYUnits: "pixels",
						src: "https://openlayers.org/en/latest/examples/data/icon.png",
					}),
				}),
			};
			const featureCustom = _opts.markers ? [routeFeature, geoMarkerInit, geoMarkerLast] : [routeFeature];
			return new VectorLayer({
				source: new VectorSource({
					features: featureCustom,
				}),
				style: function (feature) {
					return styles[feature.get("type")];
				},
			});
		} else {
			console.warn("drawPolygon: olMap is not defined");
			return false;
		}
	} else {
		console.warn("drawRoute: origin or destination must have at least item");
		return false;
	}
}

/**
 * Establece el centro en el mapa en una localización geográfica diferente.
 * @param {Array} coordinates Matriz con dos elementos: [longitud,latitud].
 * @param {number} zoom (opcional) Define un nuevo valor de zoom.
 *
 * @returns {Object} Objeto de instancia de tipo VectorLayer
 */
export function centerMap(coordinates, zoom) {
	if (olMap) {
		olMap.getView().setCenter(transform(coordinates, "EPSG:4326", "EPSG:3857"));
		if (zoom !== undefined && zoom !== null) {
			if (zoom >= 0 && zoom <= 18) {
				olMap.getView().setZoom(zoom);
			}
		}
	} else {
		console.warn("centerMap: olMap is not defined");
		return false;
	}
}

/**
 * Establece un nuevo valor de Zoom.
 * @param {number} zoom Nivel de Zoom.
 *
 * @returns {void} Valor del nuevo nivel de Zoom
 */
export function setZoom(zoom) {
	return olMap.getView().setZoom(zoom);
}

/**
 * Obtiene el valor de Zoom actual.
 *
 * @returns {number} Valor del Zoom obtenido.
 */
export function getZoom() {
	return olMap.getView().getZoom();
}

/**
 * Obtiene el objeto de la instancia de OlAnuket.
 *
 * @returns {Object} Un objeto que contiene métodos internos de OlAnuket para el manejo del mapa base.
 */
export function getOlMapObj() {
	return olMap;
}

/**
 * Obtiene la posición geográfica de un usuario, como esto puede comprometer la privacidad, la posición no está disponible a menos que el usuario la apruebe.
 *
 */
export function getCurrentPosition(callback) {
	if (olMap) {
		navigator.geolocation.getCurrentPosition(function (position) {
			try {
				const coords = {lat: position.coords.latitude, lng: position.coords.longitude};
				olMap.getView().setCenter(fromLonLat([coords.lng, coords.lat]));
				setZoom(12);
				if (typeof callback === "function") callback(coords, null);
			} catch (error) {
				console.error("getCurrentPosition: Error message", error.message);
				if (typeof callback === "function") callback(null, error);
			}
		});
	} else {
		console.warn("getCurrentPosition: olMap is not defined");
	}
}

/**
 * Descarga archivo en formato GeoJson o Kml.
 * @param {string} fileName Nombre del archivo.
 * @param {string} contentType Tipo de contenido que será retornado.
 * @property {string} type Formato de descarga(GeoJson o Kml).
 *
 */
export function download(fileName, contentType, type) {
	const typeFormat = type === "KML" ? new KML() : new GeoJSON();
	let features = [];
	olMap
		.getLayers()
		.getArray()
		.forEach(function (layer) {
			if (layer.getSource().getFeatures) {
				let featuresList = layer.getSource().getFeatures();
				for (let feature of featuresList) {
					features.push(feature);
				}
			}
		});
	const content = typeFormat.writeFeatures(features, {
		featureProjection: "EPSG:3857",
	});
	let a = document.createElement("a");
	let file = new Blob([content], {type: contentType});
	a.href = URL.createObjectURL(file);
	a.download = fileName;
	a.click();
}

/**
 * Establece los límites dados por un Objeto espacial(marcador, círculo, polígono, polilínea) en el mapa, centrándolo y fijando un Zoom sobre dichos elementos.
 * @param {Object} shape Objeto de tipo marcador, círculo, polígono o polilínea.
 *
 */
export function fitBounds(shape, type = "") {
	if (olMap) {
		if (shape === undefined || shape === null) {
			console.warn("fitBounds: Shape must be a Marker, a Circle, a Polyline or a Polygon Object.");
			return false;
		}
		if (type === "Features") {
			const transformExtent1 = transformExtent(shape, "EPSG:4326", "EPSG:3857");
			olMap.getView().fit(transformExtent1, {
				size: olMap.getSize(),
				padding: [80, 80, 80, 80],
				duration: 1000,
			});
		} else {
			if (shape.getVisible()) {
				olMap.getView().fit(shape.getSource().getExtent(), {
					size: olMap.getSize(),
					padding: [80, 80, 80, 80],
					duration: 1000,
				});
			} else {
				console.warn("fitBounds: shape is not visible");
			}
		}
	} else {
		console.warn("fitBounds: olMap is not defined");
		return false;
	}
}

/**
 * Elimina todos los marcadores que se encuentren en el mapa.
 *
 */
export function removeMarkers() {
	olMap
		.getLayers()
		.getArray()
		.forEach(function (layer) {
			if (layer.getSource().getFeatures) {
				let features = layer.getSource().getFeatures();
				for (let item of features) {
					if (item.get("type") === "Point" || item.get("type") === "MultiPoint")
						layer.getSource().removeFeature(item);
				}
			}
		});
}

/**
 * Elimina todos los polígonos que se encuentren en el mapa.
 *
 */
export function removePolygons() {
	olMap
		.getLayers()
		.getArray()
		.forEach(function (layer) {
			if (layer.getSource().getFeatures) {
				let features = layer.getSource().getFeatures();
				for (let item of features) {
					if (item.get("type") === "Polygon") layer.getSource().removeFeature(item);
				}
			}
		});
}

/**
 * Elimina todas las polilíneas que se encuentren en el mapa.
 *
 */
export function removePolylines() {
	olMap
		.getLayers()
		.getArray()
		.forEach(function (layer) {
			if (layer.getSource().getFeatures) {
				let features = layer.getSource().getFeatures();
				for (let item of features) {
					if (item.get("type") === "Polyline") layer.getSource().removeFeature(item);
				}
			}
		});
}

/**
 * Elimina todos los círculos que se encuentren en el mapa.
 *
 */
export function removeCircles() {
	olMap
		.getLayers()
		.getArray()
		.forEach(function (layer) {
			if (layer.getSource().getFeatures) {
				let features = layer.getSource().getFeatures();
				for (let item of features) {
					if (item.get("type") === "Circle") layer.getSource().removeFeature(item);
				}
			}
		});
}

/**
 * Elimina todos los marcadores, polígonos, círculos y polilineas que se encuentren en el mapa y agrega un marcador en la localización geográfica definida al inicio de la ejecución.
 *
 */
export function clearMap() {
	if (olMap) {
		removeMarkers();
		removePolygons();
		removePolylines();
		removeCircles();
		getCurrentPosition();
	}
}

/**
 * Georreferenciar una dirección.
 * @param {string} address Dirección con el formato('dirección, ciudad').
 * @param {function} callBack  Función anónima que se ejecuta al finalizar el servicio de la georreferenciación y devuelve tres parametros(coords, results, status)
 * @param {Object} options (opcional) Objeto con propiedades extras para la georreferenciación.
 * @property {boolean} options.centerMap Habilita o deshabilita la opción de centrar el mapa.
 * @property {boolean} options.marker Habilita o deshabilita la opción de establecer un marcador en el mapa.
 * @property {number} options.zoom  Valor numérico entero de 0 a 18 que establece el nivel de Zoom en el mapa.
 * @property {string} options.mapsApiKey Credencial de autenticación para usar el servicio de georreferenciación.
 *
 * @returns {Object} Objeto resultante de la georreferenciación.
 */
export async function geocoder(address, callBack, options) {
	let _opts = {
		callBack: null,
		centerMap: false,
		marker: null,
		zoom: 8,
		mapsApiKey: "",
	};
	if (address === undefined || address === null) {
		console.warn("geocoder: address must be an String");
		return false;
	}
	if (typeof callBack === "function") {
		_opts.callBack = callBack;
	} else {
		console.warn("geocoder: callback is not a valid function");
		return false;
	}
	if (options) {
		if (typeof options === "object" && Object.entries(options).length > 0) {
			if (options.mapsApiKey !== undefined) {
				if (options.mapsApiKey !== null) {
					if (options.mapsApiKey) {
						_opts.mapsApiKey = options.mapsApiKey;
					} else {
						console.warn("geocoder: Credentials are required");
						return false;
					}
				}
			}
			if (options.centerMap !== undefined) {
				if (options.centerMap !== null) {
					if (options.centerMap) {
						_opts.centerMap = options.centerMap;
					}
				}
			}
			if (options.marker !== undefined) {
				if (options.marker !== null) {
					if (options.marker) {
						_opts.marker = options.marker;
					}
				}
			}
			if (options.zoom !== undefined) {
				if (options.zoom !== null) {
					if (parseInt(options.zoom) >= 0 && parseInt(options.zoom) <= 18) {
						_opts.zoom = options.zoom;
					}
				}
			}
		} else {
			console.warn("geocoder: options is not an object or is empty");
			return false;
		}
	} else {
		console.warn("geocoder: now using current configuration");
	}
	const urlList = olMap.getLayers().getArray()[0].getSource().getUrls();
	let API_BASE_URL = "";
	if (urlList[0].indexOf("google") !== -1) {
		API_BASE_URL = `https://maps.googleapis.com/maps/api/geocode/json?key=${_opts.mapsApiKey}&address=${address}`;
	} else if (urlList[0].indexOf("hereapi") !== -1) {
		API_BASE_URL = `https://geocode.search.hereapi.com/v1/geocode?apikey=${_opts.mapsApiKey}&q=${address}`;
	} else if (urlList[0].indexOf("openstreetmap") !== -1) {
		API_BASE_URL = `https://api.openrouteservice.org/geocode/search?api_key=${_opts.mapsApiKey}&text=${address}`;
	} else {
		console.warn("No geocoding service found");
	}
	try {
		const response = await fetch(API_BASE_URL);
		if (response.ok) {
			const result = await response.json();
			let latLng = result["items"][0].position;
			if (_opts.centerMap) {
				if (olMap) {
					olMap.getView().setCenter(transform([latLng.lng, latLng.lat], "EPSG:4326", "EPSG:3857"));
				}
				if (_opts.zoom >= 0) {
					if (olMap) {
						olMap.getView().setZoom(_opts.zoom);
					}
				}
			}
			if (_opts.marker) {
				if (olMap) {
					addMarker(
						{
							lng: latLng.lng,
							lat: latLng.lat,
						},
						false
					);
				}
			}
			if (_opts.callBack !== null) {
				_opts.callBack({lng: latLng.lng, lat: latLng.lat}, result["items"][0], response.status);
			}
		} else {
			console.warn("geocoder was not successful for the following reason:", response.status);
			if (_opts.callBack !== null) {
				_opts.callBack({}, {}, response.status);
			}
			return console.error("error");
		}
	} catch (error) {
		return console.error(error.message);
	}
}

/**
 * Traducir una ubicación en el mapa (coordenadas) en una dirección legible.
 * @param {Object} coords Objeto de latitud y la longitud.
 * @param {function} callBack  Función anónima que se ejecuta al finalizar el servicio de la georreferenciación inversa y devuelve tres parametros(coords, results, status)
 * @param {Object} options (opcional) Objeto con propiedades extras para la georreferenciación inversa.
 * @property {boolean} options.centerMap Habilita o deshabilita la opción de centrar el mapa.
 * @property {boolean} options.marker Habilita o deshabilita la opción de establecer un marcador en el mapa.
 * @property {number} options.zoom  Valor numérico entero de 0 a 18 que establece el nivel de Zoom en el mapa.
 * @property {string} options.mapsApiKey Credencial de autenticación para usar el servicio de georreferenciación inversa.
 *
 * @returns {Object} Objeto resultante de la georreferenciación inversa.
 */
export async function inverseGeocoder(coords, callBack, options) {
	let _opts = {
		callBack: null,
		centerMap: false,
		marker: null,
		zoom: 8,
		mapsApiKey: "",
	};
	if (coords === undefined || coords === null) {
		console.warn("inverseGeocoder: missing parameter coords");
		return false;
	}
	if (coords.constructor != {}.constructor) {
		console.warn("inverseGeocoder: coords must be an Object");
		return false;
	}
	if (!coords.hasOwnProperty("lat") && !coords.hasOwnProperty("lng")) {
		console.warn("inverseGeocoder: missing lat or lng attributes");
		return false;
	}
	if (options) {
		if (typeof options === "object" && Object.entries(options).length > 0) {
			if (options.mapsApiKey !== undefined) {
				if (options.mapsApiKey !== null) {
					if (options.mapsApiKey) {
						_opts.mapsApiKey = options.mapsApiKey;
					} else {
						console.warn("inverseGeocoder: Credentials are required");
						return false;
					}
				}
			}
			if (options.centerMap !== undefined) {
				if (options.centerMap !== null) {
					if (options.centerMap) {
						_opts.centerMap = options.centerMap;
					}
				}
			}
			if (options.marker !== undefined) {
				if (options.marker !== null) {
					if (options.marker) {
						_opts.marker = options.marker;
					}
				}
			}
			if (options.zoom !== undefined) {
				if (options.zoom !== null) {
					if (parseInt(options.zoom) >= 0 && parseInt(options.zoom) <= 18) {
						_opts.zoom = options.zoom;
					}
				}
			}
		} else {
			console.warn("inverseGeocoder: options is not an object or is empty");
			return false;
		}
	} else {
		console.warn("inverseGeocoder: now using current configuration");
	}
	const urlList = olMap.getLayers().getArray()[0].getSource().getUrls();
	let API_BASE_URL = "";
	if (urlList[0].indexOf("google") !== -1) {
		API_BASE_URL = `https://maps.googleapis.com/maps/api/geocode/json?key=${_opts.mapsApiKey}&latlng=${
			coords.lat + "," + coords.lng
		}`;
	} else if (urlList[0].indexOf("hereapi") !== -1) {
		API_BASE_URL = `https://revgeocode.search.hereapi.com/v1/revgeocode?apikey=${_opts.mapsApiKey}&at=${
			coords.lat + "," + coords.lng
		}`;
	} else if (urlList[0].indexOf("openstreetmap") !== -1) {
		API_BASE_URL = `https://api.openrouteservice.org/geocode/reverse?api_key=${_opts.mapsApiKey}&point.lon=${coords.lat}&point.lat=${coords.lng}`;
	} else {
		console.warn("inverseGeocoder: No geocoding service found");
	}
	try {
		const response = await fetch(API_BASE_URL);
		if (response.ok) {
			const result = await response.json();
			let address = result["items"][0].address.label;
			if (_opts.centerMap) {
				if (olMap) {
					olMap.getView().setCenter(transform([coords.lng, coords.lat], "EPSG:4326", "EPSG:3857"));
				}
				if (_opts.zoom >= 0) {
					if (olMap) {
						olMap.getView().setZoom(_opts.zoom);
					}
				}
			}
			if (_opts.marker) {
				if (olMap) {
					addMarker(
						{
							lng: coords.lng,
							lat: coords.lat,
						},
						false
					);
				}
			}
			if (_opts.callBack !== null) {
				_opts.callBack(address, result["items"][0], response.status);
			}
		} else {
			console.warn("inverseGeocoder was not successful for the following reason:", response.status);
			if (_opts.callBack !== null) {
				_opts.callBack({}, {}, response.status);
			}
			return console.error("error");
		}
	} catch (error) {
		return console.error(error.message);
	}
}

/**
 * Establece vistas panorámicas a 360 grados solo para tipo de mapa google.
 * @param {Object} coords Objeto de coordenada para el posicionamiento.
 * @param {Object} options (opcional) Objeto con propiedades extras para el street view.
 * @property {string} options.container Id del contenedor.
 *
 * @returns {Object} Objeto panorama.
 */
export function streetView(coords, options) {
	const urlList = olMap.getLayers().getArray()[0].getSource().getUrls();
	if (urlList[0].indexOf("google") !== -1) {
		let _opts = {
			container: "#gpanorama",
		};
		if (typeof coords !== "object") {
			console.warn("streetView: coords is not a object");
			return false;
		}
		if (!coords.hasOwnProperty("lat") && !coords.hasOwnProperty("lng")) {
			console.warn("streetView: missing lat or lng attributes");
			return false;
		}
		if (typeof options === "object" && Object.entries(options).length > 0) {
			if (options.container !== undefined && options.container !== null) {
				if (options.container !== "") {
					_opts.container = options.container;
				}
			}
		} else {
			console.warn("streetView: options is not an object or is empty");
			return false;
		}
		return new google.maps.StreetViewPanorama(_opts.container, {
			position: {lat: coords.lat, lng: coords.lng},
		});
	} else {
		console.warn("streetView: option enabled only for google maps");
		return false;
	}
}

export function fitBoundsMarkerGroup(markerArray = []) {
	let featuresOnLayers = [];
	markerArray.forEach(function (layer) {
		featuresOnLayers.push(layer.getSource().getFeatures());
	});
	let extentOfAllFeatures = createEmpty();
	let flatFeatures = featuresOnLayers.flat();
	flatFeatures.forEach(function (feature) {
		extend(extentOfAllFeatures, feature.getGeometry().getExtent());
	});
	olMap.getView().fit(extentOfAllFeatures);
}

export function removeInteraction() {
	olMap.removeInteraction(selectInteraction);
	olMap.removeInteraction(draw);
}

export function addInteraction(type, map, callBack) {
	if (type && map) {
		removeInteraction();
		const source = map.vec.getSource();
		const styles = {
			Point: new Style({
				image: new Icon({
					anchor: [0.5, 46],
					anchorXUnits: "fraction",
					anchorYUnits: "pixels",
					src: "https://openlayers.org/en/latest/examples/data/icon.png",
				}),
			}),
			LineString: new Style({
				stroke: new Stroke({
					color: "green",
					width: 1,
				}),
			}),
			Polygon: new Style({
				stroke: new Stroke({
					color: "black",
					lineDash: [4],
					width: 1,
				}),
				fill: new Fill({
					color: "rgba(255, 255, 0, 0.2)",
				}),
			}),
			Circle: new Style({
				stroke: new Stroke({
					color: "black",
					width: 1,
				}),
				fill: new Fill({
					color: "rgba(255, 255, 0, 0.3)",
				}),
			}),
		};
		const styleFunction = function (feature) {
			feature.set("type", type);
			return styles[feature.getGeometry().getType()];
		};
		draw = new Draw({
			source: source,
			type: type,
		});
		draw.on("drawend", function (event) {
			const newShape = event.feature;
			newShape.type = type;
			callBack(newShape);
		});
		map.ol.addInteraction(draw);
		source.addFeatures(draw);
		new VectorLayer({
			map: map.ol,
			source: source,
			style: styleFunction,
		});
	} else {
		removeInteraction();
		return false;
	}
}

export async function addCartoDb(query, options) {
	let _opts = {
		title: "Current Position",
		activatePopup: true,
		onClick: null,
	};
	if (options) {
		if (typeof options === "object" && Object.entries(options).length > 0) {
			if (options.title !== null) _opts.title = options.title;
			if (options.activatePopup !== undefined) {
				if (options.activatePopup !== null) {
					if (options.activatePopup) {
						_opts.showPopup = options.activatePopup;
					}
				}
			}
			if (options.onClick !== null) {
				if (typeof options.onClick === "function") {
					_opts.onClick = options.onClick;
				}
			}
		}
	}
	const mapConfig = {
		buffersize: {mvt: 0},
		layers: [
			{
				id: "LayerCatoDb",
				options: {
					sql: query,
					interactive: ["name", "layer", "description", "cartodb_id"],
				},
			},
		],
	};
	const response = await fetch(`${serverUrl}/api/v1/map/?api_key=${apiKey}`, {
		method: "POST",
		headers: {
			Accept: "application/json",
			"Content-Type": "application/json",
		},
		body: JSON.stringify(mapConfig),
	});
	if (!response.ok) throw new Error("data not found");
	const layerGroup = await response.json();
	const tileJson = layerGroup.metadata.tilejson.vector;
	const styles = {
		Polygon: new Style({
			fill: new Fill({
				color: "rgba(255, 255, 0, 0.3)",
			}),
		}),
	};
	const styleFunction = function (feature) {
		return styles[feature.getGeometry().getType()];
	};
	let parcelsLayer = new VectorTile({
		source: new VectorTileSource({
			format: new MVT({layerName: "LayerCatoDb"}),
			tileGrid: createXYZ({maxZoom: 22}),
			tilePixelRatio: 16,
			urls: tileJson.tiles,
		}),
		style: styleFunction,
		className: "LayerCatoDb",
	});
	if (_opts.showPopup) showPopupLayer({_opts, layer: parcelsLayer}, {close: options.onClose});
	olMap.addLayer(parcelsLayer);
}

const showPopupLayer = (data, eventsMarker) => {
	let popup = new Popup();
	olMap.addOverlay(popup);
	popup.getElement().addEventListener(
		"click",
		function (e) {
			if (eventsMarker.close) eventsMarker.close();
		},
		false
	);
	olMap.on("singleclick", function (evt) {
		popup.hide();
		const trailheads = olMap.getFeaturesAtPixel(evt.pixel, {
			layerFilter: (layer) => layer === data.layer,
		});
		if (trailheads.length > 0) {
			if (typeof data._opts.onClick === "function") {
				evt.info = trailheads[0].properties_;
				data._opts.onClick(evt);
			}
			popup.show(evt.coordinate, infowindowContentLayerCarto({name: trailheads[0].properties_.name}));
		} else {
			popup.hide();
		}
	});
	olMap.on("pointermove", function (event) {
		let hit = olMap.forEachFeatureAtPixel(event.pixel, function () {
			return true;
		});
		if (hit) {
			this.getTargetElement().style.cursor = "pointer";
		} else {
			this.getTargetElement().style.cursor = "";
		}
	});
};

export async function removeTileLayers() {
	await olMap.getLayers().forEach((layer) => {
		if (layer && layer.className_ === "LayerCatoDb") olMap.removeLayer(layer);
	});
}

export function addSelectToFigure(callback) {
	// Defining Style
	let selectStyle = new Style({
		stroke: new Stroke({
			color: "#000000",
			width: 4,
		}),
		fill: new Fill({
			color: "rgba(234, 198, 11, 0.8)",
		}),
	});
	selectInteraction = new Select({
		condition: singleClick,
		layers: function (layer) {
			return layer;
		},
		style: selectStyle,
	});
	olMap.removeInteraction(selectInteraction);
	olMap.addInteraction(selectInteraction);
	selectInteraction.on(
		"select",
		(event) => {
			callback(event.target.getFeatures().getArray()[0]);
		},
		this
	);
}

export function drawGeoJSON(geojsonObject, options) {
	const styles = {
		LineString: new Style({
			stroke: new Stroke({
				width: 6,
				color: "rgba(85, 85, 85, 0.5)",
			}),
		}),
		Point: new Style({
			image: new Icon({
				anchor: [0.5, 1],
				src: "https://openlayers.org/en/latest/examples/data/icon.png",
			}),
		}),
		Polygon: new Style({
			stroke: new Stroke({
				color: "#555555",
				lineDash: [2],
				width: 1,
			}),
			fill: new Fill({
				color: "rgba(85, 85, 85, 0.5)",
			}),
		}),
		MultiPolygon: new Style({
			stroke: new Stroke({
				color: "#555555",
				width: 1,
			}),
			fill: new Fill({
				color: "rgba(85, 85, 85, 0.5)",
			}),
		}),
	};

	const styleFunction = function (feature) {
		return styles[feature.getGeometry().getType()];
	};

	const features = new GeoJSON({
		featureProjection: "EPSG:3857",
	}).readFeatures(geojsonObject);

	const vectorSource = new VectorSource({
		features,
	});

	const vectorLayer = new VectorLayer({
		source: vectorSource,
		style: styleFunction,
	});

	return vectorLayer;
}
